import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, Eye, EyeOff, Save, X, RefreshCw, BookOpen, ExternalLink } from 'lucide-react';
import {
  adminListBlogPosts,
  adminCreateBlogPost,
  adminUpdateBlogPost,
  adminDeleteBlogPost,
} from '../utils/api';

/**
 * 2026-06-10 — Admin "Blog / Hey Taxi!" panel.
 * Full CRUD: create, edit, delete, toggle draft/published. Plus image URL,
 * SEO title, and SEO description fields. Slugs are server-generated if blank.
 */

const EMPTY_DRAFT = {
  id: null,
  title: '',
  slug: '',
  category: 'Taxi-98 Updates',
  excerpt: '',
  body: '',
  imageUrl: '',
  status: 'draft',
  seoTitle: '',
  seoDescription: '',
};

const CATEGORIES = [
  'Taxi-98 Updates',
  'Airport Transfers',
  'Travel Tips',
  'Cruise Port',
  'Tours',
  'Nassau Guide',
  'Bahamas Culture',
  'Service Notice',
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return iso;
  }
};

const AdminBlogPanel = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null or draft object
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminListBlogPosts();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load admin blog posts:', err);
      setError('Failed to load blog posts. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ ...EMPTY_DRAFT });
  const startEdit = (post) => setEditing({ ...EMPTY_DRAFT, ...post });
  const cancelEdit = () => setEditing(null);

  const handleField = (name, value) => {
    setEditing(prev => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.title.trim()) {
      alert('Title is required.');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const updated = await adminUpdateBlogPost(editing.id, {
          title: editing.title,
          slug: editing.slug,
          category: editing.category,
          excerpt: editing.excerpt,
          body: editing.body,
          imageUrl: editing.imageUrl,
          status: editing.status,
          seoTitle: editing.seoTitle,
          seoDescription: editing.seoDescription,
        });
        setPosts(prev => prev.map(p => (p.id === updated.id ? updated : p)));
      } else {
        const created = await adminCreateBlogPost({
          title: editing.title,
          slug: editing.slug || undefined,
          category: editing.category,
          excerpt: editing.excerpt,
          body: editing.body,
          imageUrl: editing.imageUrl,
          status: editing.status,
          seoTitle: editing.seoTitle,
          seoDescription: editing.seoDescription,
        });
        setPosts(prev => [created, ...prev]);
      }
      setEditing(null);
    } catch (err) {
      console.error('Failed to save post:', err);
      alert(err?.response?.data?.detail || 'Failed to save post.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (post) => {
    setBusyId(post.id);
    try {
      const nextStatus = post.status === 'published' ? 'draft' : 'published';
      const updated = await adminUpdateBlogPost(post.id, { status: nextStatus });
      setPosts(prev => prev.map(p => (p.id === updated.id ? updated : p)));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update status.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setBusyId(post.id);
    try {
      await adminDeleteBlogPost(post.id);
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete post.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8" data-testid="admin-blog-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-cyan-600" />
            <span>Blog / Hey Taxi!</span>
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Create, edit, and publish posts on <code>/hey-taxi</code>. Drafts stay hidden from the public until you publish.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            data-testid="admin-blog-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={startCreate}
            data-testid="admin-blog-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg text-sm font-semibold transition shadow"
          >
            <Plus className="w-4 h-4" />
            <span>New Post</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm" data-testid="admin-blog-table">
          <thead className="bg-gray-50 text-gray-600 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="px-3 py-3 text-left">Title</th>
              <th className="px-3 py-3 text-left">Slug</th>
              <th className="px-3 py-3 text-left">Category</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Updated</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="px-3 py-10 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && posts.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-10 text-center text-gray-500">No posts yet. Click <strong>New Post</strong> to get started.</td></tr>
            )}
            {!loading && posts.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-blog-row-${p.id}`}>
                <td className="px-3 py-3 font-medium text-gray-900 max-w-[260px] truncate">{p.title}</td>
                <td className="px-3 py-3 text-gray-500 text-xs">
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded">{p.slug}</code>
                </td>
                <td className="px-3 py-3 text-gray-700">{p.category}</td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border ${
                    p.status === 'published'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  }`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(p.updatedAt)}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    {p.status === 'published' && (
                      <a
                        href={`/hey-taxi/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View on site"
                        data-testid={`admin-blog-view-${p.id}`}
                        className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => toggleStatus(p)}
                      disabled={busyId === p.id}
                      title={p.status === 'published' ? 'Unpublish (set draft)' : 'Publish'}
                      data-testid={`admin-blog-toggle-${p.id}`}
                      className="p-1.5 rounded hover:bg-gray-200 text-gray-700 disabled:opacity-50"
                    >
                      {p.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => startEdit(p)}
                      title="Edit"
                      data-testid={`admin-blog-edit-${p.id}`}
                      className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      disabled={busyId === p.id}
                      title="Delete"
                      data-testid={`admin-blog-delete-${p.id}`}
                      className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-blog-editor">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editing.id ? 'Edit Post' : 'New Post'}
              </h3>
              <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => handleField('title', e.target.value)}
                  data-testid="admin-blog-input-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                  placeholder="Post title"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Slug <span className="text-gray-400 normal-case">(optional — auto-generated if blank)</span></label>
                  <input
                    type="text"
                    value={editing.slug}
                    onChange={(e) => handleField('slug', e.target.value)}
                    data-testid="admin-blog-input-slug"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 font-mono text-sm"
                    placeholder="auto-from-title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Category</label>
                  <input
                    type="text"
                    list="admin-blog-categories"
                    value={editing.category}
                    onChange={(e) => handleField('category', e.target.value)}
                    data-testid="admin-blog-input-category"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                    placeholder="Travel Tips"
                  />
                  <datalist id="admin-blog-categories">
                    {CATEGORIES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Excerpt</label>
                <textarea
                  rows={2}
                  value={editing.excerpt}
                  onChange={(e) => handleField('excerpt', e.target.value)}
                  data-testid="admin-blog-input-excerpt"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 resize-none"
                  placeholder="One or two-sentence summary that appears in cards and search results."
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Body <span className="text-gray-400 normal-case">(HTML allowed — use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;a&gt;, &lt;strong&gt;)</span></label>
                <textarea
                  rows={12}
                  value={editing.body}
                  onChange={(e) => handleField('body', e.target.value)}
                  data-testid="admin-blog-input-body"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 font-mono text-sm resize-y"
                  placeholder="<p>Your post content here…</p>"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Image URL <span className="text-gray-400 normal-case">(optional — placeholder shown if blank)</span></label>
                <input
                  type="text"
                  value={editing.imageUrl}
                  onChange={(e) => handleField('imageUrl', e.target.value)}
                  data-testid="admin-blog-input-image"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 text-sm"
                  placeholder="https://…/image.jpg"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">SEO Title</label>
                  <input
                    type="text"
                    value={editing.seoTitle}
                    onChange={(e) => handleField('seoTitle', e.target.value)}
                    data-testid="admin-blog-input-seo-title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 text-sm"
                    placeholder="Defaults to post title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">Status</label>
                  <select
                    value={editing.status}
                    onChange={(e) => handleField('status', e.target.value)}
                    data-testid="admin-blog-input-status"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 bg-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">SEO Description</label>
                <textarea
                  rows={2}
                  value={editing.seoDescription}
                  onChange={(e) => handleField('seoDescription', e.target.value)}
                  data-testid="admin-blog-input-seo-desc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 resize-none text-sm"
                  placeholder="Defaults to excerpt. Aim for 150–160 characters."
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="admin-blog-save"
                className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-semibold text-sm shadow disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving…' : (editing.id ? 'Save Changes' : 'Create Post')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogPanel;
